/**
 * QueryBoundary — the four states every list screen must handle.
 *
 * The house rule for this app, applied literally on every screen:
 *   LOADING  → a skeleton or spinner, never a blank page;
 *   ERROR    → the API's Vietnamese message + its error code + a retry;
 *   OFFLINE  → say so explicitly, because "không tải được" while the wifi is
 *              down is a different problem from a server error and the user
 *              can act on exactly one of them;
 *   EMPTY    → an empty state that says what to do next, not a bare table.
 *
 * Wrapping this once means a screen cannot ship having handled only the happy
 * path, which is the single most common way an internal tool feels broken.
 */
import type { ReactNode } from 'react';
import { ErrorState, LoadingState, EmptyState, TableSkeleton } from '@sonauto/ui';
import { isApiError } from '@sonauto/api-client';
import { errorCode, errorMessage, errorRequestId } from '../lib/api.js';

export interface QueryBoundaryProps<T> {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: T | undefined;
  readonly onRetry?: () => void;
  /** True when the result set is empty; drives the empty state. */
  readonly isEmpty?: (data: T) => boolean;
  readonly emptyTitle?: string;
  readonly emptyDescription?: ReactNode;
  readonly emptyAction?: ReactNode;
  /** `table` renders a table-shaped skeleton instead of a spinner. */
  readonly loadingVariant?: 'spinner' | 'table';
  readonly children: (data: T) => ReactNode;
}

export function QueryBoundary<T>({
  isLoading,
  isError,
  error,
  data,
  onRetry,
  isEmpty,
  emptyTitle = 'Chưa có dữ liệu',
  emptyDescription,
  emptyAction,
  loadingVariant = 'spinner',
  children,
}: QueryBoundaryProps<T>): React.JSX.Element {
  if (isLoading && data === undefined) {
    return loadingVariant === 'table' ? <TableSkeleton /> : <LoadingState />;
  }

  if (isError && data === undefined) {
    // OFFLINE is a distinct, actionable state — not a generic failure.
    if (isApiError(error) && error.isConnectivityError) {
      return (
        <ErrorState
          title="Đang ngoại tuyến"
          message="Không kết nối được máy chủ. Dữ liệu sẽ tự tải lại khi có mạng."
          code={errorCode(error)}
          {...(onRetry !== undefined ? { onRetry } : {})}
        />
      );
    }
    return (
      <ErrorState
        message={errorMessage(error)}
        code={errorCode(error)}
        requestId={errorRequestId(error)}
        {...(onRetry !== undefined ? { onRetry } : {})}
      />
    );
  }

  if (data === undefined) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  if (isEmpty?.(data) === true) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;
  }

  return <>{children(data)}</>;
}
